import * as React from 'react';
import { getCourtPageUrl } from 'constants/pages';
import { getPlatform } from 'utils/mobile/getPlatform';
import { getVenueCoverImage } from 'utils/shared/venues';
import { useModal } from 'hooks/useModal';
import AddressRow from 'screens/ClubOverview/components/ClubProfileAside/AddressRow';
import AmenitiesList from 'screens/ClubOverview/components/ClubProfileAside/AmenitiesList';
import ClubIdentity from 'screens/ClubOverview/components/ClubProfileAside/ClubIdentity';
import ContactCard from 'screens/ClubOverview/components/ClubProfileAside/ContactCard';
import CoverBanner from 'screens/ClubOverview/components/ClubProfileAside/CoverBanner';
import { getAccessSummary } from 'screens/ClubOverview/constants';
import { ButtonLink } from 'components/Button';
import ModalShare from 'components/modals/ModalShare';
import classNames from 'styles/utils/classNames';
import AsideActions from './AsideActions';
import { CourtPageVenue } from '../../types';

interface Props {
  venue: CourtPageVenue;
  isFollowing: boolean;
  isFollowDisabled: boolean;
  onToggleFollow: () => void;
  /**
   * Wraps amenities + contact — the part the mobile "Club info" tab owns, so the
   * page can hide it behind the other tabs while the header stays put. Desktop
   * has no tabs and passes nothing.
   */
  detailsClassName?: string;
  /** Mobile tab switcher, rendered directly under the address. */
  tabsSlot?: React.ReactNode;
}

export default function ClubAside({
  venue,
  isFollowing,
  isFollowDisabled,
  onToggleFollow,
  detailsClassName,
  tabsSlot,
}: Props) {
  const {
    isOpen: isShareOpen,
    openModal: openShareModal,
    closeModal: closeShareModal,
  } = useModal();

  const amenities = venue.amenities.map((entry) => entry.amenity);
  const customAmenities = venue.customAmenities.map((entry) => entry.name);
  const coverImage = getVenueCoverImage(venue.images);

  const shareUrl = `${process.env.APP_URL}${getCourtPageUrl(
    venue.id,
  )}?utm_source=share&utm_medium=${getPlatform()}utm_campaign=share`;

  const handleShare = () => {
    openShareModal();

    if (navigator.share) {
      navigator
        .share({
          title: `${venue.title} on Bounce`,
          text: `Play pickleball at ${venue.title}.`,
          url: shareUrl,
        })
        .catch(() => {});
    }
  };

  return (
    <aside className="flex flex-col gap-6">
      <AsideActions
        isFollowing={isFollowing}
        isFollowDisabled={isFollowDisabled}
        onToggleFollow={onToggleFollow}
        onShare={handleShare}
      />

      <CoverBanner imageUrl={coverImage?.url} clubName={venue.title} />

      <ClubIdentity
        clubName={venue.title}
        accessSummary={getAccessSummary(venue.facilityType, venue.accessType)}
        hasTennis={venue.hasTennis}
        hasPickleball={venue.hasPickleball}
        hasPadel={venue.hasPadel}
      />

      {venue.addressString && <AddressRow address={venue.addressString} />}

      {tabsSlot}

      <div className={classNames('flex flex-col gap-6', detailsClassName)}>
        <AmenitiesList amenities={amenities} customAmenities={customAmenities} />

        <ContactCard
          websiteUrl={venue.websiteUrl}
          email={venue.email}
          phoneNumber={venue.phoneNumber}
          instagramUrl={venue.instagramUrl}
          facebookUrl={venue.facebookUrl}
          youtubeUrl={venue.youtubeUrl}
          linkedinUrl={venue.linkedinUrl}
          twitterUrl={venue.twitterUrl}
          cta={
            venue.email ? (
              <ButtonLink href={`mailto:${venue.email}`} variant="brand" size="md" isInline>
                Contact club
              </ButtonLink>
            ) : undefined
          }
        />
      </div>

      <ModalShare
        isOpen={isShareOpen}
        closeModal={closeShareModal}
        title="Share court"
        shareUrl={shareUrl}
      />
    </aside>
  );
}
